import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { PRIVACY, TERMS } from "../../data/legal";
import { NAV, LINKEDIN_URL, CONTACT_EMAIL, SERVICES } from "../../data/site";
import { scrollToSection, LANDING_PATHS } from "../../lib/scroll";
import { Close, LinkedIn } from "../ui/Icons";
import "./Footer.css";

function LegalModal({ doc, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!doc) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [doc, onClose]);

  return (
    <AnimatePresence>
      {doc && (
        <motion.div
          className="modal__bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="legal-title"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.25 }}
          >
            <div className="modal__head">
              <h2 id="legal-title" className="h3">
                {doc.title}
              </h2>
              <button
                ref={closeRef}
                type="button"
                className="modal__close"
                onClick={onClose}
                aria-label="Close"
              >
                <Close />
              </button>
            </div>
            <div className="modal__body">{doc.body}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Footer() {
  const [doc, setDoc] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const year = new Date().getFullYear();

  const goSection = (id) => {
    const path = id === "home" ? "/" : `/${id}`;
    if (LANDING_PATHS.includes(location.pathname)) {
      navigate(path, { state: { fromHeaderClick: true } });
      scrollToSection(id);
    } else {
      navigate(path);
    }
  };

  return (
    <footer className="ftr">
      <div className="container">
        <div className="ftr__grid">
          <div className="ftr__brand">
            <button type="button" className="ftr__logo" onClick={() => goSection("home")}>
              <img src="/logo.png" alt="" width="34" height="34" />
              <span>
                Episcope<em>AI</em>
              </span>
            </button>
            <p className="ftr__tag">
              Decoding vision, defining possibilities. Enterprise-grade computer vision for
              industrial, urban and security operations.
            </p>
            <div className="ftr__social">
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Episcope AI on LinkedIn"
              >
                <LinkedIn />
              </a>
              <a href={`mailto:${CONTACT_EMAIL}`} className="mono">
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>

          <div className="ftr__col">
            <h4 className="mono">Product</h4>
            <ul>
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <RouterLink to={s.path}>{s.title}</RouterLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="ftr__col">
            <h4 className="mono">Company</h4>
            <ul>
              {NAV.map((n) => (
                <li key={n.id}>
                  <button type="button" onClick={() => goSection(n.id)}>
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="ftr__col">
            <h4 className="mono">Legal</h4>
            <ul>
              <li>
                <button type="button" onClick={() => setDoc(PRIVACY)}>
                  Privacy Policy
                </button>
              </li>
              <li>
                <button type="button" onClick={() => setDoc(TERMS)}>
                  Terms of Service
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="ftr__bottom">
          <span>© {year} Episcope AI. All rights reserved.</span>
          <button type="button" className="mono ftr__top" onClick={() => goSection("home")}>
            Back to top ↑
          </button>
        </div>
      </div>
      <LegalModal doc={doc} onClose={() => setDoc(null)} />
    </footer>
  );
}
